import { z } from 'zod';
import { MoneyCentsSchema } from './money.js';
import { PaymentMethodSchema } from './enums.js';
export const CreateSaleItemSchema = z.object({
    productId: z.number().int().positive(),
    qty: z.number().int().min(1).max(10_000),
    unitPriceCents: z.number().int().min(0).max(9_999_999).optional(),
});
export const CreateSaleRequestSchema = z.object({
    items: z.array(CreateSaleItemSchema).min(1).max(100),
    discountCents: z.number().int().min(0).default(0),
    paymentMethod: PaymentMethodSchema,
    note: z
        .string()
        .trim()
        .max(280)
        .optional()
        .transform((v) => (v === '' ? null : (v ?? null))),
});
/** Same shape as creating a sale (spec §3) - items plus an optional manual discount. */
export const PreviewSaleRequestSchema = CreateSaleRequestSchema;
export const SaleItemSchema = z.object({
    id: z.number().int(),
    productId: z.number().int(),
    productName: z.string(),
    /** The product's current stock code (joined live; the name above is the sale-time snapshot). */
    code: z.string(),
    unitPriceCents: MoneyCentsSchema,
    qty: z.number().int(),
    lineTotalCents: MoneyCentsSchema,
});
export const SaleSummarySchema = z.object({
    id: z.number().int(),
    soldBy: z.number().int(),
    /**
     * Frozen at sale creation - the seller's display name AT THE TIME of
     * this sale, never the account's current name (renaming/handing over
     * the account afterwards never rewrites this). See docs/DECISIONS.md.
     */
    soldByName: z.string(),
    /** Snapshot short code, e.g. "U-7F3A2C" - same code across a rename, same code across a handover. */
    soldByCode: z.string(),
    paymentMethod: PaymentMethodSchema,
    subtotalCents: MoneyCentsSchema,
    /** The MANUAL discount only. See `autoDiscountCents` for the automatic one. */
    discountCents: MoneyCentsSchema,
    totalCents: MoneyCentsSchema,
    /** The automatic discount the server computed at sale time, 0 if none applied. */
    autoDiscountCents: MoneyCentsSchema,
    autoDiscountRuleId: z.number().int().nullable(),
    /** Snapshot label, e.g. "Festive keychains · 20% off" - survives the rule being edited/deleted. */
    autoDiscountLabel: z.string().nullable(),
    itemCount: z.number().int(),
    note: z.string().nullable(),
    createdAt: z.string(),
    voidedAt: z.string().nullable(),
    voidedBy: z.number().int().nullable(),
    /** Frozen at void time, same snapshot story as `soldByName`/`soldByCode`. Null until voided. */
    voidedByName: z.string().nullable(),
    voidedByCode: z.string().nullable(),
});
export const SaleDetailSchema = SaleSummarySchema.extend({
    items: z.array(SaleItemSchema),
});
export const SaleListResponseSchema = z.object({
    sales: z.array(SaleSummarySchema),
    total: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
});
//# sourceMappingURL=sales.js.map